import type { BlockTransfer } from '../types';

/**
 * How a group came under focus. The pointer and the keyboard each hold their own, so leaving
 * with one doesn't drop the group the other still holds.
 */
export type FocusSource = 'pointer' | 'keyboard';

/** What the transfer lines emphasise: these lines lit, and these groups' bars outlined. */
export interface LineEmphasis {
    lineKeys: ReadonlySet<string>;
    groups: ReadonlySet<number>;
}

/**
 * How far a focused group's emphasis reaches. The board uses the default, its direct parents;
 * the others are there for when the design asks for them.
 */
export interface FocusScope {
    /** `parents`: the lines into the group; `all`: into its parents too, back to the roots. */
    ancestors: 'parents' | 'all';
    /** Also the lines out of the group, to its direct children. */
    children: boolean;
}

export const DEFAULT_FOCUS_SCOPE: FocusScope = { ancestors: 'parents', children: false };

/** A hovered (or end-focused) line: it and the groups at its two ends. */
export function transferEmphasis(transfer: BlockTransfer): LineEmphasis {
    return {
        lineKeys: new Set([transfer.key]),
        groups: new Set([transfer.sourceId, transfer.destinationId]),
    };
}

/**
 * A hovered or focused group: the lines that brought its fish, and the groups they came from,
 * with the group itself. Lines that stay on one row (a chain's continuation) aren't drawn, so
 * they light nothing, but their source still counts as a parent.
 */
export function groupEmphasis(
    transfers: readonly BlockTransfer[],
    groupId: number,
    scope: FocusScope = DEFAULT_FOCUS_SCOPE
): LineEmphasis {
    const lineKeys = new Set<string>();
    const groups = new Set<number>([groupId]);
    const frontier = [groupId];
    while (frontier.length > 0) {
        const id = frontier.pop();
        for (const transfer of transfers) {
            if (transfer.destinationId !== id) continue;
            lineKeys.add(transfer.key);
            if (scope.ancestors === 'all' && !groups.has(transfer.sourceId)) {
                frontier.push(transfer.sourceId);
            }
            groups.add(transfer.sourceId);
        }
    }
    if (scope.children) {
        for (const transfer of transfers) {
            if (transfer.sourceId !== groupId) continue;
            lineKeys.add(transfer.key);
            groups.add(transfer.destinationId);
        }
    }
    return { lineKeys, groups };
}
